import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Camera, ArrowRight, Phone } from "lucide-react";
import SeoHead from "../components/SeoHead";
import CTABand from "../components/CTABand";
import { PageHeader } from "./Services";
import { PHOTOS, PHOTO_CATEGORIES, getPhotosByCategory } from "../lib/photos";
import { SITE } from "../lib/site";

export default function Photos() {
  const [active, setActive] = useState("Tous");
  const list = useMemo(() => getPhotosByCategory(active), [active]);

  const schema = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    name: "Photos chantiers Termite Habitat 33 — Bordeaux & Gironde",
    description:
      "Photothèque des interventions réelles de Termite Habitat 33 en Gironde : essaimages, cordonnets, pièges-appâts, bois infestés, chantiers.",
    image: PHOTOS.map((p) => ({
      "@type": "ImageObject",
      contentUrl: p.src,
      caption: p.title,
      description: p.description,
    })),
  };

  return (
    <div data-testid="photos-page">
      <SeoHead
        title="Photos chantiers termites Bordeaux & Gironde | Termite Habitat 33"
        description="Photothèque de chantiers réels Termite Habitat 33 : essaimages, cordonnets de terre, pièges-appâts Termigard, bois infestés, charpentes anciennes traitées, barrières chimiques en pied de mur et planche anatomique termite. 42 photos d'expertises en Gironde (2024-2026)."
        canonical={SITE.url + "/photos"}
        keywords={[
          "photos termites Bordeaux",
          "photos chantier termites Gironde",
          "essaimage termites photo",
          "cordonnets termites photo",
          "pièges-appâts photo",
        ]}
        breadcrumbs={[
          { name: "Accueil", url: SITE.url + "/" },
          { name: "Photos", url: SITE.url + "/photos" },
        ]}
        schema={schema}
      />
      <PageHeader
        eyebrow="Photothèque chantiers"
        title="Nos interventions en photos."
        intro="Photos prises sur des chantiers réels d'expertise et de traitement termites en Gironde : essaimages, cordonnets de terre, pièges-appâts, bois infestés, charpentes attaquées."
      />

      {/* Category filter */}
      <section className="border-b border-[#D5CABD] bg-white sticky top-0 z-10">
        <div className="container-x py-4 flex flex-wrap gap-2" data-testid="photos-filter">
          {PHOTO_CATEGORIES.map((c) => {
            const isActive = c === active;
            return (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`text-xs uppercase tracking-widest px-3 py-2 border rounded-sm transition ${
                  isActive
                    ? "bg-[#7A2E39] text-white border-[#7A2E39]"
                    : "bg-white text-gray-700 border-[#D5CABD] hover:border-[#7A2E39]"
                }`}
                data-testid={`photos-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </section>

      {/* Grid */}
      <section className="container-x py-12" data-testid="photos-grid">
        <div className="text-xs text-gray-500 mb-5">
          {list.length} photo{list.length > 1 ? "s" : ""}{active !== "Tous" ? ` — ${active}` : ""}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((p, i) => (
            <figure
              key={p.src}
              className="group bg-white border border-[#D5CABD] overflow-hidden rounded-sm flex flex-col"
              data-testid={`photo-card-${i}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                <img
                  src={p.src}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <figcaption className="p-5 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-widest text-[#7A2E39] mb-1">
                  {p.cat} · {p.location} · {new Date(p.date).toLocaleDateString("fr-FR")}
                </div>
                <h2 className="font-serif text-lg text-gray-900 mb-2">{p.title}</h2>
                <p className="text-sm text-gray-700">{p.description}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="container-x py-16 max-w-3xl text-center">
        <Camera className="w-10 h-10 text-[#7A2E39] mx-auto mb-3" />
        <h2 className="font-serif text-3xl text-gray-900 mb-4">
          Vous reconnaissez l'un de ces signes chez vous&nbsp;?
        </h2>
        <p className="text-gray-700 mb-6">
          Si vous observez essaimages, cordonnets, bois creux ou pièges actifs autour de votre
          habitat, n'attendez pas. Demandez un devis gratuit en ligne ou appelez-nous directement.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/devis" className="btn-primary" data-testid="photos-devis-link">
            Demander un devis gratuit <ArrowRight className="w-4 h-4" />
          </Link>
          <a href={`tel:${SITE.phoneRaw}`} className="btn-ghost" data-testid="photos-call">
            <Phone className="w-4 h-4" /> {SITE.phone}
          </a>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
